import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Ban, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { adminUpdateBookingNotes } from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAgendaTab = ({
  currentWeekStart, weekDays, prevWeek, nextWeek, goToToday,
  bookingsByDatetime, blockedSet, toggleSlotBlock,
  blockEntireDay, openEntireDay, bookings, setBookings, TIME_SLOTS,
  formatDate, formatDateStr, isToday, token, adminSlots
}) => {

  const [remindersData, setRemindersData] = React.useState({});
  const [sendingReminder, setSendingReminder] = React.useState(null);

  React.useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/admin/reminders`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          const map = {};
          data.forEach(r => { map[r.bookingId] = r; });
          setRemindersData(map);
        }
      })
      .catch(() => {});
  }, [token]);

  const sendManualReminder = async (bookingId) => {
    setSendingReminder(bookingId);
    try {
      const res = await fetch(`${API_URL}/api/admin/reminders/send-now/${bookingId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Rappel envoyé avec succès');
        // Refresh reminders
        const r2 = await fetch(`${API_URL}/api/admin/reminders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await r2.json();
        if (Array.isArray(data)) {
          const map = {};
          data.forEach(r => { map[r.bookingId] = r; });
          setRemindersData(map);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur envoi rappel');
      }
    } catch (e) { toast.error('Erreur réseau'); }
    setSendingReminder(null);
  };

  const getReminderBadge = (booking) => {
    const info = remindersData[booking.bookingId || booking.id];
    if (!info) return null;

    if (booking.confirmed_by_candidate || info.confirmed_by_candidate) {
      return <span data-testid={`reminder-confirmed-${booking.bookingId}`} style={{ display: 'inline-block', padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700, background: '#dcfce7', color: '#166534', marginTop: '2px' }}>✓ Confirmé</span>;
    }
    if (info.reminders_sent && info.reminders_sent.length > 0) {
      const count = info.reminders_sent.filter(r => r.success).length;
      return <span data-testid={`reminder-sent-${booking.bookingId}`} style={{ display: 'inline-block', padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700, background: '#dbeafe', color: '#1e40af', marginTop: '2px' }}>{count} rappel{count > 1 ? 's' : ''}</span>;
    }
    return null;
  };

  const toggleBookingType = async (booking) => {
    const newType = booking.type === 'decouverte' ? 'formule' : 'decouverte';
    try {
      await fetch(`${API_URL}/api/admin/bookings/${booking.bookingId || booking.id}/type`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type: newType })
      });
      booking.type = newType;
      if (setBookings) setBookings([...bookings]);
      toast.success(newType === 'decouverte'
        ? 'Marqué Découverte (ne compte pas dans les séances)'
        : 'Marqué Formule (compte dans les séances)');
    } catch (err) { toast.error('Erreur'); }
  };
  return (
          <div data-testid="admin-agenda">
            {/* Week navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <button onClick={prevWeek} className="btn-secondary" style={{ padding: '0.5rem' }}><ChevronLeft size={20} /></button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <h2 className="heading-3" style={{ margin: 0 }}>
                  {weekDays[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — {weekDays[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </h2>
                <button onClick={goToToday} className="btn-secondary" style={{ padding: '0.35rem 0.75rem', fontSize: '0.7rem' }}>Aujourd'hui</button>
              </div>
              <button onClick={nextWeek} className="btn-secondary" style={{ padding: '0.5rem' }}><ChevronRight size={20} /></button>
            </div>

            {/* Week grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '70px repeat(7, 1fr)', gap: '1px', background: 'var(--border-light)', borderRadius: '12px', overflow: 'hidden' }}>
              {/* Header row */}
              <div style={{ background: 'var(--bg-section)', padding: '0.75rem 0.5rem', fontWeight: 600, fontSize: '0.7rem', textAlign: 'center' }}>Heure</div>
              {weekDays.map((day, i) => (
                <div key={i} style={{
                  background: isToday(day) ? '#fce4ec' : 'var(--bg-section)',
                  padding: '0.75rem 0.5rem', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center'
                }}>
                  {day.toLocaleDateString('fr-FR', { weekday: 'short' })}<br />
                  <span style={{ fontSize: '1rem' }}>{day.getDate()}</span>
                </div>
              ))}

              {/* Time slots */}
              {TIME_SLOTS.map(time => (
                <React.Fragment key={time}>
                  <div style={{ background: 'var(--bg-card)', padding: '0.5rem', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {time}
                  </div>
                  {weekDays.map((day, di) => {
                    const dateStr = formatDateStr(day);
                    const key = `${dateStr}|${time}`;
                    const explicitlyBlocked = blockedSet.has(key);
                    const dayBookings = bookingsByDatetime[key] || [];
                    const hasBooking = dayBookings.length > 0;

                    // Check if admin explicitly opened this slot
                    const explicitlyOpened = adminSlots && adminSlots.some(s => s.date === dateStr && s.time === time && !s.blocked);

                    // Default blocked rules
                    const isSunday = day.getDay() === 0;
                    const isSaturday = day.getDay() === 6;
                    const hour = parseInt(time.split(':')[0], 10);
                    const defaultBlocked = isSunday || (isSaturday && hour >= 12);

                    const isBlocked = explicitlyBlocked || (defaultBlocked && !explicitlyOpened);

                    return (
                      <div key={di} style={{
                        background: isBlocked ? '#fef2f2' : hasBooking ? '#f0fdf4' : 'var(--bg-card)',
                        padding: '0.35rem',
                        minHeight: '55px',
                        fontSize: '0.65rem',
                        position: 'relative'
                      }}>
                        {hasBooking && dayBookings.map((b, bi) => (
                          <div key={bi} style={{
                            background: b.type === 'decouverte' ? '#fef3c7' : '#dcfce7',
                            border: `1px solid ${b.type === 'decouverte' ? '#f59e0b' : '#86efac'}`,
                            borderRadius: '6px',
                            padding: '0.25rem 0.4rem', marginBottom: '2px', lineHeight: 1.3,
                            cursor: 'pointer'
                          }}
                          onClick={(e) => { e.stopPropagation(); toggleBookingType(b); }}
                          title={b.type === 'decouverte' ? 'Découverte — Cliquer pour changer en Formule' : 'Formule — Cliquer pour changer en Découverte'}
                          >
                            <strong>{b.candidateInfo?.prenom || b.userName || '—'}</strong><br />
                            <span style={{ color: b.type === 'decouverte' ? '#92400E' : '#166534' }}>{b.programName || b.programId}</span>
                            {b.type === 'decouverte' && <><br /><span style={{ color: '#B45309', fontWeight: 700, fontSize: '0.6rem' }}>DÉCOUVERTE</span></>}
                            {b.candidateInfo?.telephone && <><br /><span style={{ color: '#E75480', fontSize: '0.7rem' }}>{b.candidateInfo.telephone}</span></>}
                            {getReminderBadge(b)}
                          </div>
                        ))}
                        {isBlocked && !hasBooking && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#dc2626', opacity: 0.5 }}>
                            <Ban size={16} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: '#dcfce7', border: '1px solid #86efac', display: 'inline-block' }} /> Formule (compte)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: '#fef3c7', border: '1px solid #f59e0b', display: 'inline-block' }} /> Découverte (ne compte pas)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: '#fef2f2', border: '1px solid #fca5a5', display: 'inline-block' }} /> Bloqué
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--bg-card)', border: '1px solid var(--border-light)', display: 'inline-block' }} /> Disponible
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: '#dbeafe', border: '1px solid #93c5fd', display: 'inline-block' }} /> Rappel envoyé
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: '#dcfce7', border: '1px solid #86efac', display: 'inline-block' }} /> Confirmé par candidat
              </span>
              <span style={{ fontSize: '0.7rem', fontStyle: 'italic', color: '#94A3B8' }}>
                Cliquez sur un RDV pour basculer Formule/Découverte
              </span>
            </div>

            {/* Upcoming bookings list */}
            <h3 className="heading-3" style={{ marginTop: '2.5rem', marginBottom: '1rem' }}>Prochaines réservations</h3>
            {bookings.filter(b => b.status === 'confirmed').slice(0, 10).map(b => (
              <div key={b.bookingId} className="card" style={{ padding: '1rem', marginBottom: '0.75rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }} data-testid={`booking-${b.bookingId}`}>
                <div style={{ width: 50, height: 50, borderRadius: '10px', background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CalendarDays size={22} style={{ color: '#E75480' }} />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {b.candidateInfo ? `${b.candidateInfo.prenom} ${b.candidateInfo.nom}` : (b.userName || '—')}
                  </div>
                  {b.candidateInfo?.email && (
                    <div className="body-small" style={{ color: '#E75480', fontWeight: 500 }}>
                      {b.candidateInfo.email}
                    </div>
                  )}
                  {b.candidateInfo?.telephone && (
                    <div className="body-small" style={{ color: '#E75480', fontWeight: 500 }}>
                      {b.candidateInfo.telephone}
                    </div>
                  )}
                  <div className="body-small" style={{ color: 'var(--text-muted)' }}>
                    {b.programName || b.programId} · Séance n°{b.sessionNumber}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {new Date(b.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                  <div className="body-small" style={{ color: 'var(--text-muted)' }}>{b.time}</div>
                  {getReminderBadge(b)}
                  <button
                    data-testid={`toggle-type-${b.bookingId}`}
                    onClick={() => toggleBookingType(b)}
                    title={b.type === 'decouverte' ? 'Cliquez pour changer en Formule' : 'Cliquez pour changer en Découverte'}
                    style={{
                      marginTop: '0.5rem', padding: '0.35rem 0.85rem', borderRadius: '1rem', border: '2px solid',
                      cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
                      background: b.type === 'decouverte' ? '#FEF3C7' : '#DBEAFE',
                      color: b.type === 'decouverte' ? '#92400E' : '#1E40AF',
                      borderColor: b.type === 'decouverte' ? '#F59E0B' : '#3B82F6',
                      transition: 'all 0.2s'
                    }}
                  >
                    {b.type === 'decouverte' ? 'Découverte' : 'Formule'}
                  </button>
                  <button
                    data-testid={`send-reminder-${b.bookingId}`}
                    onClick={() => sendManualReminder(b.bookingId)}
                    disabled={sendingReminder === b.bookingId}
                    style={{
                      display: 'block', marginTop: '0.35rem', padding: '0.3rem 0.75rem', borderRadius: '0.5rem',
                      border: '1px solid #E75480', background: sendingReminder === b.bookingId ? '#fce4ec' : 'white',
                      color: '#E75480', cursor: sendingReminder === b.bookingId ? 'wait' : 'pointer',
                      fontSize: '0.7rem', fontWeight: 600, width: '100%'
                    }}
                  >
                    {sendingReminder === b.bookingId ? 'Envoi...' : 'Envoyer rappel'}
                  </button>
                </div>
                {b.candidateInfo && (
                  <div style={{ width: '100%', background: '#fef9c3', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.75rem' }}>
                    <strong>Livret 1 :</strong> {b.candidateInfo.livret1Depose} · <strong>R\u00e9daction :</strong> {b.candidateInfo.redactionDebutee} · <strong>Besoin :</strong> {b.candidateInfo.besoin}
                  </div>
                )}
                <div style={{ width: '100%' }} data-testid={`booking-notes-${b.bookingId}`}>
                  <textarea
                    defaultValue={b.adminNotes || ''}
                    placeholder="Notes de suivi du RDV..."
                    onBlur={async (e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = '#f9fafb';
                      const val = e.target.value;
                      if (val !== (b.adminNotes || '')) {
                        try {
                          await adminUpdateBookingNotes(token, b.bookingId || b.id, val);
                          b.adminNotes = val;
                          toast.success('Notes du RDV sauvegard\u00e9es');
                        } catch (err) {
                          toast.error('Erreur sauvegarde notes');
                        }
                      }
                    }}
                    style={{
                      width: '100%', minHeight: '60px', padding: '0.5rem 0.75rem', border: '1px solid #e5e7eb',
                      borderRadius: '6px', fontSize: '0.78rem', resize: 'vertical', outline: 'none',
                      fontFamily: 'inherit', lineHeight: '1.4', background: '#f9fafb'
                    }}
                    onFocus={e => { e.target.style.borderColor = '#667eea'; e.target.style.background = '#fff'; }}
                  />
                  {b.adminNotes_updated_at && (
                    <p style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: '0.2rem' }}>
                      Modifi\u00e9 le {new Date(b.adminNotes_updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>
            ))}
            {bookings.filter(b => b.status === 'confirmed').length === 0 && (
              <p className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Aucune réservation pour le moment.</p>
            )}
          </div>

  );
};

export default AdminAgendaTab;
